/**
 * Раздел «Библиотека конструкций» — прайс лаборатории.
 *
 * Четыре вкладки: аппараты, доп. элементы, материалы, цвета. Позиции
 * сгруппированы по категориям; названия категорий и цены правятся по клику.
 *
 * Две цены у каждой позиции (§1) — наличными и по договору. Вторая не
 * выводится из первой: у части позиций они равны, а у категории
 * «Не в прайсе — уточнить цену» обе нулевые.
 *
 * Отличие от демо: колонка категории показана. В демо её не было, и перенести
 * позицию в другую категорию было нельзя. Здесь категория — запись со ссылками,
 * и удалить непустую нельзя, поэтому переносить позиции обязательно должно быть
 * возможно.
 */

import { useMemo, useState } from 'react';
import { Check, FileText, Pencil, Plus, Search, Trash2 , Send } from 'lucide-react';
import { formatKop, rublesToKop } from '@otc/shared';
import { useBootstrap, useOrders } from '../api/queries.js';
import { useDrafts } from '../store/drafts.js';
import { useSync } from '../store/useSync.js';
import { useModule } from '../store/useModule.js';
import { Button, Modal, SectionHeader, Spinner, Toast, useToast } from '../components/ui.js';
import { SaveDiscard } from '../components/SaveDiscard.js';
import { ConflictModal } from '../components/GuardModals.js';
import { EditableText } from '../components/EditableText.js';
import { TinyConfirm, Toggle } from '../components/Toggle.js';
import { Swatch } from '../components/Swatch.js';
import { ColorEditor } from '../components/ColorEditor.js';
import { ConstructionIcon } from '../components/icons.js';
import { useFillHeight } from '../components/useFillHeight.js';
import { useTableZoom } from '../components/useTableZoom.js';
import { ZoomControl } from '../components/ZoomControl.js';
import { FitToWidth } from '../components/FitToWidth.js';
import { printHtml } from '../components/printHtml.js';
import { PriceListModal } from '../components/PriceListModal.js';
import { SendColorMapDialog } from '../components/SendDialogs.js';
import { ConstructionStats } from './ConstructionStats.js';

interface ConstructionRow {
  id: string; version: number;
  type: 'Базовая конструкция' | 'Дополнительный элемент';
  categoryId: string; name: string;
  /** Описание изделия — второй столбец прайс-листа. */
  description: string;
  priceCashKop: number; priceContractKop: number; costKop: number;
  hasColor: boolean;
  /** Разрешено ли заказывать несколько одинаковых штук. */
  qtyAdjustable: boolean;
}

interface ColorRow {
  id: string; version: number; categoryId: string | null;
  name: string; art: string; hex: string; image: string | null; unspecified: boolean;
  fill: 'SOLID' | 'GLITTER' | 'IRIS' | 'RAINBOW';
  glitterHex: string;
}
interface CategoryRow { id: string; version: number; kind: 'base' | 'extra' | 'color'; name: string; sortOrder: number }

type Tab = 'base' | 'extra' | 'colors' | 'stats';

/*
 * Вкладки «Материалы» здесь нет: лаборатория ведёт себестоимость одной суммой
 * в колонке «Расход», а разбирать её по материалам не стала — за всё время
 * заполнено 3 связи из шести материалов. Справочник и его таблицы в базе
 * остались нетронутыми, убран только раздел интерфейса.
 */
const TABS: { id: Tab; label: string }[] = [
  { id: 'base', label: 'Аппараты' },
  { id: 'extra', label: 'Доп. элементы' },
  { id: 'colors', label: 'Цвета' },
  /*
   * «Статистика» — сколько чего сделали помесячно.
   *
   * Стоит в конце и рядом со справочником нарочно: считает она ровно те
   * позиции, что перечислены слева, и смотреть их удобно тут же, не уходя
   * в «Финансы» с их деньгами.
   */
  { id: 'stats', label: 'Статистика' },
];

export function Constructions() {
  const bootstrap = useBootstrap(true);
  const module = useModule('constructions');

  const { toast, show } = useToast();

  const [tab, setTab] = useState<Tab>('base');
  /*
   * Заказы нужны только «Статистике» — и запрашиваются только на ней:
   * справочник открывают десятки раз в день, и тащить полторы тысячи заказов
   * ради вкладки, куда заходят раз в месяц, незачем.
   */
  const ordersQuery = useOrders(null, tab === 'stats');
  const [query, setQuery] = useState('');
  /*
   * Окно прайс-листа: просмотр и правка листа, как у наряда. Прайс сперва
   * ПОКАЗЫВАЕТСЯ, а скачивается по желанию — чаще его нужно просто посмотреть.
   */
  const [priceOpen, setPriceOpen] = useState(false);
  /** Окно отправки карты цветов врачам. */
  const [colorSend, setColorSend] = useState(false);

  useSync('constructions', 'construction', bootstrap.data?.constructions as never);
  useSync('constructions', 'color', bootstrap.data?.colors as never);
  useSync('constructions', 'category', bootstrap.data?.categories as never);

  const draftState = useDrafts((state) => state.modules.constructions);
  const store = () => useDrafts.getState();

  const constructions = useMemo(() => {
    void draftState;
    return store().entities<ConstructionRow>('constructions', 'construction');
  }, [draftState]);
  const colors = useMemo(() => {
    void draftState;
    return store().entities<ColorRow>('constructions', 'color');
  }, [draftState]);
  const categories = useMemo(() => {
    void draftState;
    return store().entities<CategoryRow>('constructions', 'category');
  }, [draftState]);

  const update = (kind: string, id: string, patch: Record<string, unknown>) =>
    store().update('constructions', kind, id, patch);

  const save = async () => {
    const outcome = await module.save();
    /* Удача — молча. */
    if (outcome.blocked) show(outcome.blocked[0]!.message, 'error');
    else if (outcome.error) show(outcome.error, 'error');
  };

  /* Листается справочник, а не страница: поиск, вкладки, «Прайс-лист» и
     «Сохранить» остаются на месте. Подробности — в `useFillHeight`. */
  const { ref: списокRef, высота: высотаСписка } = useFillHeight();

  /* Масштаб справочника: у каждого раздела свой, запоминается в браузере. */
  const zoom = useTableZoom('constructions', true);

  if (bootstrap.isLoading) return <Spinner />;

  const kind = tab === 'base' ? 'base' : tab === 'extra' ? 'extra' : 'color';
  const tabCategories = categories
    .filter((c) => c.kind === kind)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ru'));

  const wantedType = tab === 'base' ? 'Базовая конструкция' : 'Дополнительный элемент';
  const q = query.trim().toLowerCase();
  const items = constructions
    .filter((k) => k.type === wantedType)
    .filter((k) => !q || k.name.toLowerCase().includes(q));

  return (
    <div>
      <SectionHeader
        icon={ConstructionIcon}
        title="Библиотека конструкций"
        right={
          <>
            {/*
              Поиск и прайс ОТДЕЛЕНЫ чертой от сохранения — как в «Заказах»,
              «Врачах» и «Клиниках». Это работа со справочником: найти позицию,
              посмотреть прайс. «Сохранить» и «Отменить» относятся ко всему
              разделу разом, и в общем ряду через одинаковый зазор четыре
              кнопки читались как равные — рука шла не туда.
            */}
            {/*
              На телефоне поиск ТЯНЕТСЯ, а «Прайс-лист» держит свою ширину.
              При жёстких 176 пикселях поля кнопке оставалось меньше её
              собственной подписи, и та переносилась на две строки — кнопка
              вырастала вдвое выше поля рядом.
            */}
            <span
              className="flex min-w-0 flex-1 items-center gap-2 md:mr-2 md:flex-none md:border-r md:pr-4"
              style={{ borderColor: 'var(--otc-line)' }}
            >
              {(tab === 'base' || tab === 'extra') && (
                /* Лупа внутри поля. Обёртка берёт на себя ровно те правила
                   ширины, что были у поля: на телефоне тянется, на широком
                   экране 11rem — иначе строка с «Прайс-листом» перестроилась
                   бы и кнопка снова ломалась бы на две строки. */
                <span className="relative block min-w-0 flex-1 md:w-44 md:flex-none">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--otc-ink2)' }}
                  />
                  <input
                    className="w-full rounded-lg border px-3 py-1.5 text-sm"
                    style={{
                      borderColor: 'var(--otc-line)',
                      background: 'var(--otc-surface)',
                      paddingLeft: '2.1rem',
                    }}
                    placeholder="Поиск…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </span>
              )}
              {/*
                Прайс-лист собирается СЕРВЕРОМ из этого же справочника.

                Раньше он жил отдельным файлом Word, который правили руками:
                подняли цену здесь — а у врача на руках прайс со старой, и
                расхождение вылезало на счёте.

                Кнопка ОТКРЫВАЕТ прайс на экране, а не отдаёт файл. Ссылку в
                новую вкладку пришлось убрать: установленное приложение таких
                вкладок не создаёт, оно передаёт адрес системному браузеру, а тот
                про вход в программу не знает — вместо прайса выходило
                «Требуется вход в систему». Скачать PDF можно из самого окна.
              */}
              {/*
                «Отправить» у ЦВЕТОВ — карта цветов врачам.

                Только на этой вкладке: карту шлют тому, кто выбирает цвет
                с пациентом, а прайс-лист уходит своей кнопкой из своего окна.
              */}
              {tab === 'colors' && (
                <button
                  title="Отправить карту цветов врачам"
                  onClick={() => setColorSend(true)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors hover:bg-black/5 md:px-3.5 md:py-2 md:text-sm"
                  style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
                >
                  <Send size={14} />
                  <span className="whitespace-nowrap">Отправить</span>
                </button>
              )}
              {/*
                Прайс-лист — только на «Аппаратах» и «Доп. элементах».

                Он собирается из цен этих двух справочников, а на «Цветах» и
                «Статистике» цен нет вовсе: кнопка там предлагала открыть то,
                что к вкладке не относится. Рядом с ней на «Цветах» уже стоит
                своя «Отправить» — карта цветов, и две кнопки про разные
                документы путали.
              */}
              {(tab === 'base' || tab === 'extra') && (
                /* Белая кнопка с рамкой — как «Сводный отчёт» в «Заказах». */
                <button
                  title="Посмотреть и поправить прайс-лист"
                  onClick={() => setPriceOpen(true)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors hover:bg-black/5 md:px-3.5 md:py-2 md:text-sm disabled:opacity-60"
                  style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
                >
                  <FileText size={14} />
                  <span className="whitespace-nowrap">Прайс-лист</span>
                </button>
              )}
              {/* Масштаб общий на все вкладки раздела: справочник, цвета и
                  статистика — одна рабочая область, и размер у неё один. */}
              <ZoomControl zoom={zoom} hint="Мельче — на экран влезет больше позиций" />
            </span>
            <SaveDiscard
              dirty={module.dirty}
              saving={module.saving}
              onSave={() => void save()}
              onDiscard={() => {
                module.discard();
                show('Изменения отменены');
              }}
              what="Конструкции"
            />
          </>
        }
      />

      {/*
        Вкладки — КАК В «ДОКУМЕНТАХ»: на телефоне в два столбца, на широком
        экране полосой. В одну строку четыре названия не помещались:
        «Доп. элементы» ломалось пополам, а «Статистика» уезжала за край.
      */}
      <div
        className="mb-5 grid grid-cols-2 gap-1 rounded-lg p-1 md:flex md:w-fit"
        style={{ background: 'var(--otc-surface)', border: '1px solid var(--otc-line)' }}
      >
        {TABS.map((item) => (
          <button
            key={item.id}
            onClick={() => setTab(item.id)}
            className="whitespace-nowrap rounded-md px-3.5 py-2 text-sm md:py-1.5"
            style={{
              background: tab === item.id ? 'var(--otc-accent-dark)' : 'transparent',
              color: tab === item.id ? '#fff' : 'var(--otc-ink2)',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/*
        Прокручивается СПИСОК ПОЗИЦИЙ. Вкладки и поиск при этом остаются
        на виду: в базовых конструкциях четыре десятка позиций по
        категориям, и, долистав до последней, за поиском приходилось
        возвращаться наверх.
      */}
      <div
        ref={списокRef}
        className="-mx-1 px-1"
        style={{ maxHeight: высотаСписка ?? undefined, overflowY: высотаСписка ? 'auto' : undefined }}
      >
      {/* Масштаб — на содержимом: высоту прокручиваемого блока подбирает
          `useFillHeight` под окно, и сжимать её вместе со списком нельзя. */}
      <div style={{ zoom: `${zoom.zoom}%` }}>
      {(tab === 'base' || tab === 'extra') && (
        <CategoryGroups
          categories={tabCategories}
          items={items}
          /*
            Расход ведётся и у аппаратов, и у доп. элементов: печать модели
            тратит пластик и время печатника — это такие же затраты, и
            в себестоимость заказа они теперь входят наравне с базой.
          */
          showCost={tab === 'base' || tab === 'extra'}
          /*
            Разрешение на количество — тоже только у аппаратов. Доп. элементы
            заказывают штуками всегда: винтов и крючков в аппарат идёт
            несколько, и переключатель у них ничего бы не решал.
          */
          showQty={tab === 'base'}
          /* Доп. элементы не бывают цветными — переключателя у них нет. */
          showColor={tab === 'base'}
          onUpdateItem={(id, patch) => update('construction', id, patch)}
          onRenameCategory={(id, name) => update('category', id, { name })}
          onRemoveCategory={(id) => store().remove('constructions', 'category', id)}
          onAddCategory={() =>
            store().create('constructions', 'category', {
              kind, name: 'Новая категория', sortOrder: tabCategories.length,
            })
          }
          onAddItem={(categoryId) =>
            store().create('constructions', 'construction', {
              type: wantedType, categoryId, name: 'Новая позиция', description: '',
              // Цвет по умолчанию есть: цветных позиций в прайсе большинство.
              priceCashKop: 0, priceContractKop: 0, costKop: 0, hasColor: true, qtyAdjustable: false,
            })
          }
          onRemoveItem={(id) => store().remove('constructions', 'construction', id)}
        />
      )}

      {tab === 'colors' && (
        <ColorsSection
          colors={colors}
          categories={tabCategories}
          onUpdate={(id, patch) => update('color', id, patch)}
          onAdd={(categoryId) =>
            store().create('constructions', 'color', {
              categoryId, name: 'Новый цвет', art: '', hex: '#CCCCCC', image: null,
              fill: 'SOLID', glitterHex: '#FFFFFF',
            })
          }
          onRemove={(id) => store().remove('constructions', 'color', id)}
          onRenameCategory={(id, name) => update('category', id, { name })}
          onAddCategory={() =>
            store().create('constructions', 'category', {
              kind: 'color', name: 'Новая категория', sortOrder: tabCategories.length,
            })
          }
        />
      )}
      </div>
      </div>

      {tab === 'stats' && (
        /* Статистика лежит вне прокручиваемого блока — масштаб ей нужен свой
           такой же, иначе при переключении вкладки размер бы скакал. */
        <div style={{ zoom: `${zoom.zoom}%` }}>
          <ConstructionStats
            orders={ordersQuery.data?.orders}
            constructions={bootstrap.data?.constructions ?? []}
            загружается={ordersQuery.isLoading}
          />
        </div>
      )}

      {module.conflicts && (
        <ConflictModal
          conflicts={module.conflicts}
          onReload={() => {
            module.discard();
            module.dismissConflicts();
            void bootstrap.refetch();
          }}
          onKeepMine={() => void module.keepMine()}
          onClose={module.dismissConflicts}
        />
      )}

      {colorSend && (
        <SendColorMapDialog
          onClose={() => setColorSend(false)}
          onDone={(текст) => {
            show(текст);
            setColorSend(false);
          }}
        />
      )}

      {priceOpen && (
        <PriceListModal
          onClose={() => setPriceOpen(false)}
          onError={(m) => show(m, 'error')}
          onSaved={(m) => {
            show(m);
            setPriceOpen(false);
          }}
          // Отправка писем прайс не меняет — окно остаётся открытым.
          onNotify={(m) => show(m)}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

// ---------------------------------------------------------------------------

const MONO = { fontFamily: "'IBM Plex Mono', monospace", color: 'var(--otc-ink)' };

/** Ширина задаётся через `colgroup`, здесь её быть не должно — иначе конфликт. */
function Th({ children, sticky }: { children: React.ReactNode; last?: boolean; sticky?: boolean }) {
  return (
    <th
      className={`whitespace-nowrap px-2 py-2 text-left text-xs font-semibold text-white md:px-3 ${
        sticky ? 'sticky left-0 z-20' : ''
      }`}
      // Своя заливка обязательна: прозрачная ячейка пропускает под себя
      // прокручиваемое содержимое, и заголовок читается поверх чисел.
      style={sticky ? { background: 'var(--otc-table-head)' } : undefined}
    >
      {children}
    </th>
  );
}

/** Позиции справочника, сгруппированные по категориям. */
function CategoryGroups({
  categories,
  items,
  showCost,
  showQty,
  showColor,
  onUpdateItem,
  onRenameCategory,
  onRemoveCategory,
  onAddCategory,
  onAddItem,
  onRemoveItem,
}: {
  categories: CategoryRow[];
  items: ConstructionRow[];
  showCost: boolean;
  /** Можно ли разрешать заказ нескольких штук. Только у аппаратов. */
  showQty: boolean;
  showColor: boolean;
  onUpdateItem: (id: string, patch: Record<string, unknown>) => void;
  onRenameCategory: (id: string, name: string) => void;
  onRemoveCategory: (id: string) => void;
  onAddCategory: () => void;
  onAddItem: (categoryId: string) => void;
  onRemoveItem: (id: string) => void;
}) {
  const [confirmCategory, setConfirmCategory] = useState<string | null>(null);
  const [confirmItem, setConfirmItem] = useState<string | null>(null);

  /** Позиции, чья категория не попала в список — чтобы они не исчезли с экрана. */
  const known = new Set(categories.map((c) => c.id));
  const orphans = items.filter((k) => !known.has(k.categoryId));

  return (
    <div className="space-y-5">
      {categories.map((category) => {
        const list = items.filter((k) => k.categoryId === category.id);

        return (
          <div key={category.id}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <EditableText
                value={category.name}
                onSave={(value) => onRenameCategory(category.id, String(value))}
                textClassName="text-xs font-semibold uppercase tracking-wide"
                textStyle={{ color: 'var(--otc-ink2)' }}
                inputWidth={200}
              />
              <span className="text-xs text-ink2">({list.length})</span>

              <button
                onClick={() => onAddItem(category.id)}
                className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs hover:bg-black/5"
                style={{ color: 'var(--otc-accent)' }}
                title="Добавить позицию в эту категорию"
              >
                <Plus size={12} /> Добавить
              </button>

              {confirmCategory === category.id ? (
                <DeleteCategoryConfirm
                  itemCount={list.length}
                  others={categories.filter((c) => c.id !== category.id)}
                  onConfirm={(moveToId) => {
                    // Позиции переносятся ДО удаления категории. Порядок важен:
                    // сервер обрабатывает изменения раньше удалений, поэтому
                    // обе операции проходят одним сохранением.
                    if (moveToId) {
                      for (const item of list) onUpdateItem(item.id, { categoryId: moveToId });
                    }
                    onRemoveCategory(category.id);
                    setConfirmCategory(null);
                  }}
                  onCancel={() => setConfirmCategory(null)}
                />
              ) : (
                <button
                  onClick={() => setConfirmCategory(category.id)}
                  className="flex-shrink-0 rounded-md border p-1 hover:bg-black/5"
                  style={{ borderColor: 'var(--otc-line)' }}
                  title="Удалить категорию"
                >
                  <Trash2 size={11} className="text-ink2" />
                </button>
              )}
            </div>

            {list.length > 0 ? (
              <ItemsTable
                list={list}
                showCost={showCost}
                showQty={showQty}
                showColor={showColor}
                confirmItem={confirmItem}
                setConfirmItem={setConfirmItem}
                onUpdateItem={onUpdateItem}
                onRemoveItem={onRemoveItem}
              />
            ) : (
              <p className="text-xs text-ink2">Пусто</p>
            )}
          </div>
        );
      })}

      {orphans.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--otc-coral)' }}>
            Без категории ({orphans.length})
          </p>
          <ItemsTable
            list={orphans}
            showCost={showCost}
            showQty={showQty}
            showColor={showColor}
            confirmItem={confirmItem}
            setConfirmItem={setConfirmItem}
            onUpdateItem={onUpdateItem}
            onRemoveItem={onRemoveItem}
          />
        </div>
      )}

      <Button variant="secondary" onClick={onAddCategory}>
        <Plus size={13} /> Добавить категорию
      </Button>
    </div>
  );
}

/**
 * Таблица позиций категории.
 *
 * Колонки категории здесь НЕТ намеренно: позиции и так сгруппированы по
 * категориям заголовками, и повторять её в каждой строке — лишний столбец
 * с одинаковым значением. Перенести позицию в другую категорию можно при
 * удалении категории — там предлагается выбрать, куда переложить содержимое.
 */
function ItemsTable({
  list,
  showCost,
  showQty,
  showColor,
  confirmItem,
  setConfirmItem,
  onUpdateItem,
  onRemoveItem,
}: {
  list: ConstructionRow[];
  /** Колонка «Расход» — во что позиция обходится лаборатории. */
  showCost: boolean;
  showQty: boolean;
  /** Признак «требует выбора цвета» есть только у базовых конструкций. */
  showColor: boolean;
  confirmItem: string | null;
  setConfirmItem: (id: string | null) => void;
  onUpdateItem: (id: string, patch: Record<string, unknown>) => void;
  onRemoveItem: (id: string) => void;
}) {
  /**
   * Какая карточка сейчас правится — только на ТЕЛЕФОНЕ.
   *
   * В таблице на широком экране каждое значение правится на месте, по
   * нажатию: места хватает всем колонкам сразу. На карточке столько полей
   * не помещается, и описание для прайса там не показывалось вовсе — его
   * нельзя было ни прочесть, ни стереть. Поэтому правка вынесена в один
   * карандаш: он раскрывает карточку в форму с названием, ценами, расходом
   * и описанием, а закрытая карточка остаётся короткой.
   */
  const [editingId, setEditingId] = useState<string | null>(null);

  /**
   * Ширины колонок заданы явно.
   *
   * Каждая категория рисуется ОТДЕЛЬНОЙ таблицей, а при автоматической
   * раскладке ширина подбирается по содержимому — колонки соседних категорий
   * не совпадали и «ступеньками» разъезжались по вертикали. Фиксированная
   * раскладка выравнивает их все.
   */
  /*
   * Названию отдано меньше, деньгам больше. Прежние доли оставляли колонке
   * цены около 57 пикселей полезной ширины — «11 000 ₽» туда не влезало, и
   * знак рубля переносился под число в каждой строке. Название от этого не
   * страдает: оно прилипло к левому краю и видно всегда, а переносится оно
   * по словам и так.
   */
  /*
    Пятая колонка («Цвет / кол-во») есть только у аппаратов, поэтому ширины
    для расхода задаются двумя наборами: у доп. элементов последняя колонка
    пустая и держит лишь корзину.
  */
  const columns = showCost
    ? showColor
      ? ['34%', '15%', '15%', '15%', '21%']
      : ['40%', '17%', '17%', '17%', '9%']
    : showColor
      ? ['44%', '18%', '18%', '20%']
      : ['52%', '20%', '20%', '8%'];

  /** Корзина строки: своя для карточки и для таблицы, логика одна. */
  const removeControlFor = (id: string) =>
    confirmItem === id ? (
      <TinyConfirm
        onConfirm={() => {
          onRemoveItem(id);
          setConfirmItem(null);
        }}
        onCancel={() => setConfirmItem(null)}
      />
    ) : (
      <button
        onClick={() => setConfirmItem(id)}
        className="otc-btn otc-btn-ghost otc-btn-icon"
        title="Удалить позицию"
      >
        <Trash2 size={13} className="text-ink2" />
      </button>
    );

  return (
    <>
      {/*
        На телефоне справочник — КАРТОЧКАМИ, а не таблицей.

        Пять колонок требуют шестисот точек, а экран даёт триста семьдесят:
        «Расход» и «Цвет / кол-во» уезжали за край целиком, и добраться до них
        можно было только боковой прокруткой — отдельной у каждой из десяти
        категорий. На карточке всё, что у позиции есть, видно сразу.

        Таблица никуда не делась: от 768 пикселей показывается она.
      */}
      <div className="space-y-1.5 md:hidden">
        {list.map((item) => (
          <div
            key={item.id}
            className="rounded-xl border px-3 py-2"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            {editingId === item.id ? (
              /*
                Правка карточки — обычными полями, без подтверждающих
                галочек у каждого: правки копятся черновиком раздела и
                уходят общей кнопкой «Сохранить» наверху, как и всё
                остальное здесь.
              */
              <div className="space-y-2">
                <label className="block">
                  <span className="mb-0.5 block text-[10px] text-ink2">Название</span>
                  <input
                    autoFocus
                    className="otc-input text-sm"
                    value={item.name}
                    onChange={(e) => onUpdateItem(item.id, { name: e.target.value })}
                  />
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="mb-0.5 block text-[10px] text-ink2">Наличные, ₽</span>
                    <input
                      className="otc-input tabular text-sm"
                      inputMode="decimal"
                      value={item.priceCashKop / 100}
                      onChange={(e) =>
                        onUpdateItem(item.id, { priceCashKop: rublesToKop(e.target.value) })
                      }
                    />
                  </label>
                  <label className="block">
                    <span className="mb-0.5 block text-[10px] text-ink2">Договор, ₽</span>
                    <input
                      className="otc-input tabular text-sm"
                      inputMode="decimal"
                      value={item.priceContractKop / 100}
                      onChange={(e) =>
                        onUpdateItem(item.id, { priceContractKop: rublesToKop(e.target.value) })
                      }
                    />
                  </label>
                </div>

                {showCost && (
                  <label className="block">
                    <span className="mb-0.5 block text-[10px] text-ink2">Расход, ₽</span>
                    <input
                      className="otc-input tabular text-sm"
                      inputMode="decimal"
                      value={item.costKop / 100}
                      onChange={(e) =>
                        onUpdateItem(item.id, { costKop: rublesToKop(e.target.value) })
                      }
                    />
                  </label>
                )}

                {/*
                  Описание — единственное поле, которого на телефоне не было
                  вовсе. Пустым оно бывает чаще, чем заполненным, поэтому
                  стирается свободно: набранное на пробу слово иначе
                  осталось бы в прайсе навсегда.
                */}
                <label className="block">
                  <span className="mb-0.5 block text-[10px] text-ink2">Описание для прайса</span>
                  <textarea
                    className="otc-input resize-none text-sm"
                    rows={2}
                    placeholder="Не обязательно"
                    value={item.description}
                    onChange={(e) => onUpdateItem(item.id, { description: e.target.value })}
                  />
                </label>

                <button
                  onClick={() => setEditingId(null)}
                  className="otc-btn otc-btn-ghost"
                  style={{ borderColor: 'var(--otc-line)' }}
                >
                  <Check size={14} /> Готово
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-1">
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-[13px] font-medium text-ink">
                      {item.name}
                    </span>
                    {/*
                      Описание — МЕЛКИМ шрифтом под названием: это пояснение
                      к позиции для прайса, а не вторая её строка. Пустое —
                      не показывается вовсе, чтобы не занимать высоту.
                    */}
                    {item.description && (
                      <span className="mt-0.5 block break-words text-[11px] leading-snug text-ink2">
                        {item.description}
                      </span>
                    )}
                  </span>
                  {/*
                    Карандаш и корзина — ОДНОЙ и той же кнопкой-значком.

                    Сначала карандаш был сделан проще и мельче корзины, и они
                    разъезжались по высоте: ряд выровнен по верху, а у кнопок
                    разная высота — корзина висела ниже и ряд читался как
                    сбитый. Общий вид кнопок держит их на одной линии сам.
                  */}
                  <span className="flex flex-shrink-0 items-center gap-0.5">
                    <button
                      onClick={() => setEditingId(item.id)}
                      className="otc-btn otc-btn-ghost otc-btn-icon"
                      title="Изменить название, цены и описание"
                      aria-label="Изменить позицию"
                    >
                      <Pencil size={13} className="text-ink2" />
                    </button>
                    {removeControlFor(item.id)}
                  </span>
                </div>

                {/*
                  Цены — двумя колонками с подписями. Подписи обязательны:
                  без шапки таблицы три числа подряд читались бы как одно
                  и то же, и «расход» легко принять за цену.
                */}
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <span className="min-w-0">
                    <span className="block text-[10px] leading-none text-ink2">Наличные</span>
                    <span className="tabular text-sm text-ink">
                      {formatKop(item.priceCashKop)} ₽
                    </span>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] leading-none text-ink2">Договор</span>
                    <span className="tabular text-sm text-ink">
                      {formatKop(item.priceContractKop)} ₽
                    </span>
                  </span>
                  {showCost && (
                    <span className="min-w-0">
                      <span className="block text-[10px] leading-none text-ink2">Расход</span>
                      <span className="tabular text-sm text-ink2">
                        {formatKop(item.costKop)} ₽
                      </span>
                    </span>
                  )}
                </div>
              </>
            )}
            {/*
              Переключатели с ПОДПИСЯМИ: в таблице их смысл держала шапка
              «Цвет / кол-во», на карточке шапки нет, и два одинаковых
              рычажка подряд не объясняли бы ничего.
            */}
            {(showColor || showQty) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                {showColor && (
                  <span className="flex items-center gap-1.5">
                    <Toggle
                      checked={item.hasColor}
                      onChange={() => onUpdateItem(item.id, { hasColor: !item.hasColor })}
                      title="Требует выбора цвета"
                    />
                    <span className="text-[11px] text-ink2">выбор цвета</span>
                  </span>
                )}
                {showQty && (
                  <span className="flex items-center gap-1.5">
                    <Toggle
                      checked={item.qtyAdjustable}
                      onChange={() => onUpdateItem(item.id, { qtyAdjustable: !item.qtyAdjustable })}
                      title="Изменяемое количество — в заказе появится счётчик штук"
                    />
                    <span className="text-[11px] text-ink2">количество</span>
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

    <div className="hidden overflow-x-auto rounded-xl border md:block" style={{ borderColor: 'var(--otc-line)' }}>
      <table
        // Мельче на телефоне: при ширине колонки в 13 % «11 000 ₽» не
        // помещалось в строку и знак рубля уезжал под число.
        className="w-full text-xs md:text-sm"
        style={{ borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 600 }}
      >
        <colgroup>
          {columns.map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <thead>
          <tr style={{ background: 'var(--otc-table-head)' }}>
            <Th sticky>Наименование</Th>
            <Th>Наличные</Th>
            <Th>Договор</Th>
            {showCost && <Th>Расход</Th>}
            <Th last>{showColor ? 'Цвет / кол-во' : ''}</Th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, index) => {
            const rowBg = index % 2 ? '#FBFAF7' : 'var(--otc-surface)';

            /*
             * Удаление позиции. На телефоне стоит РЯДОМ С НАЗВАНИЕМ, в той же
             * прилипшей ячейке: таблица шире экрана, и в своём законном месте
             * справа корзина оставалась за краем — её приходилось искать
             * прокруткой, не понимая, какую строку удаляешь. На широком экране
             * она остаётся справа, рядом с остальными действиями строки.
             */
            const removeControl =
              confirmItem === item.id ? (
                <TinyConfirm
                  onConfirm={() => {
                    onRemoveItem(item.id);
                    setConfirmItem(null);
                  }}
                  onCancel={() => setConfirmItem(null)}
                />
              ) : (
                <button
                  onClick={() => setConfirmItem(item.id)}
                  className="otc-btn otc-btn-ghost otc-btn-icon"
                  title="Удалить позицию"
                >
                  <Trash2 size={13} className="text-ink2" />
                </button>
              );

            return (
              <tr key={item.id} style={{ borderTop: '1px solid var(--otc-line)', background: rowBg }}>
                {/*
                  Название ПРИЛИПАЕТ к левому краю. Таблица шире телефона и
                  прокручивается вбок; без этого при прокрутке оставались одни
                  цены, переключатели и корзина — без строки, к которой они
                  относятся. Удалить было можно, но не понять что именно.
                */}
                <td
                  className="sticky left-0 z-10 px-2 py-2 md:px-3 md:py-2.5"
                  style={{ background: rowBg, borderRight: '1px solid var(--otc-line)' }}
                >
                  <div className="flex items-start gap-1">
                    <span className="min-w-0 flex-1">
                      <EditableText
                        value={item.name}
                        onSave={(value) => onUpdateItem(item.id, { name: String(value) })}
                        textClassName="break-words"
                        textStyle={{ color: 'var(--otc-ink)' }}
                        inputWidth={240}
                      />
                      {/*
                        Описание — ВТОРОЙ СТРОКОЙ под названием, а не отдельным
                        столбцом.

                        Столбец сделал бы таблицу ещё шире, а она и так не
                        помещается в телефон и прокручивается вбок. К тому же
                        описание — пояснение к названию, а не самостоятельная
                        величина в ряду цен: в прайсе оно и печатается рядом
                        с названием.

                        Пустое описание показывается подсказкой «описание для
                        прайса» — иначе строка выглядела бы просто пустой, и
                        никто бы не догадался, что тут можно писать.
                      */}
                      <span className="mt-0.5 block">
                        <EditableText
                          value={item.description}
                          onSave={(value) => onUpdateItem(item.id, { description: String(value) })}
                          placeholder="Описание для прайса"
                          textClassName="break-words text-[11px] leading-snug"
                          textStyle={{ color: 'var(--otc-ink2)' }}
                          inputWidth={240}
                          multiline
                          /* Описание бывает и пустым — стереть его надо давать. */
                          allowEmpty
                        />
                      </span>
                    </span>
                    <span className="flex-shrink-0 md:hidden">{removeControl}</span>
                  </div>
                </td>

                <td className="px-2 py-2 md:px-3 md:py-2.5">
                  <EditableText
                    value={formatKop(item.priceCashKop)}
                    onSave={(value) => onUpdateItem(item.id, { priceCashKop: rublesToKop(String(value)) })}
                    textStyle={MONO}
                    suffix="₽"
                    inputWidth={100}
                  />
                </td>
                <td className="px-2 py-2 md:px-3 md:py-2.5">
                  <EditableText
                    value={formatKop(item.priceContractKop)}
                    onSave={(value) => onUpdateItem(item.id, { priceContractKop: rublesToKop(String(value)) })}
                    textStyle={MONO}
                    suffix="₽"
                    inputWidth={100}
                  />
                </td>

                {showCost && (
                  <td className="px-2 py-2 md:px-3 md:py-2.5">
                    <EditableText
                      value={formatKop(item.costKop)}
                      onSave={(value) => onUpdateItem(item.id, { costKop: rublesToKop(String(value)) })}
                      textStyle={{ ...MONO, color: 'var(--otc-ink2)' }}
                      suffix="₽"
                      inputWidth={100}
                    />
                  </td>
                )}

                <td className="px-2 py-2 md:px-3 md:py-2.5">
                  <div className="flex items-center gap-2">
                    {showColor && (
                      <Toggle
                        checked={item.hasColor}
                        onChange={() => onUpdateItem(item.id, { hasColor: !item.hasColor })}
                        title="Требует выбора цвета"
                      />
                    )}
                    {/*
                      Изменяемое количество разрешается ПОШТУЧНО и только
                      аппаратам: у большинства пара одинаковых бессмысленна —
                      их делают по слепку конкретного пациента, — и счётчик
                      в заказе рядом с ними только провоцировал бы ошибку.
                      
                      Доп. элементам переключатель не нужен вовсе: их всегда
                      заказывают штуками, счётчик у них стоял с самого начала.
                    */}
                    {showQty && (
                      <Toggle
                        checked={item.qtyAdjustable}
                        onChange={() => onUpdateItem(item.id, { qtyAdjustable: !item.qtyAdjustable })}
                        title="Изменяемое количество — в заказе появится счётчик штук"
                      />
                    )}
                    <span className="hidden md:inline-flex">{removeControl}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * Подтверждение удаления категории.
 *
 * Непустую категорию удалить нельзя — на её позиции ссылается справочник,
 * и сервер такое отклонит. Поэтому вместо отказа предлагается выбрать, куда
 * переложить содержимое: одно действие вместо «сначала перенесите вручную,
 * потом возвращайтесь».
 */
function DeleteCategoryConfirm({
  itemCount,
  others,
  onConfirm,
  onCancel,
}: {
  itemCount: number;
  others: CategoryRow[];
  onConfirm: (moveToId: string | null) => void;
  onCancel: () => void;
}) {
  const [moveTo, setMoveTo] = useState(others[0]?.id ?? '');

  if (itemCount === 0) {
    return (
      <span
        className="flex items-center gap-1.5 rounded-md border px-2 py-1"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        <span className="text-xs" style={{ color: 'var(--otc-coral)' }}>Удалить категорию?</span>
        <TinyConfirm onConfirm={() => onConfirm(null)} onCancel={onCancel} />
      </span>
    );
  }

  // Переложить некуда: последнюю категорию с позициями удалить нельзя.
  if (others.length === 0) {
    return (
      <span
        className="flex items-center gap-1.5 rounded-md border px-2 py-1"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        <span className="text-xs" style={{ color: 'var(--otc-coral)' }}>
          Это единственная категория — позиции переложить некуда
        </span>
        <button onClick={onCancel} className="otc-btn otc-btn-secondary otc-btn-sm" style={{ borderColor: 'var(--otc-line)' }}>
          ✕
        </button>
      </span>
    );
  }

  return (
    <span
      className="flex flex-wrap items-center gap-1.5 rounded-md border px-2 py-1"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <span className="text-xs" style={{ color: 'var(--otc-coral)' }}>
        Удалить, а {itemCount} позиц. перенести в:
      </span>
      <select
        className="rounded border px-1.5 py-0.5 text-xs"
        style={{ borderColor: 'var(--otc-line)', color: 'var(--otc-ink)', background: 'transparent' }}
        value={moveTo}
        onChange={(e) => setMoveTo(e.target.value)}
      >
        {others.map((category) => (
          <option key={category.id} value={category.id}>{category.name}</option>
        ))}
      </select>
      <TinyConfirm onConfirm={() => onConfirm(moveTo)} onCancel={onCancel} />
    </span>
  );
}

// ---------------------------------------------------------------------------

function ColorsSection({
  colors,
  categories,
  onUpdate,
  onAdd,
  onRemove,
  onRenameCategory,
  onAddCategory,
}: {
  colors: ColorRow[];
  categories: CategoryRow[];
  onUpdate: (id: string, patch: Record<string, unknown>) => void;
  onAdd: (categoryId: string) => void;
  onRemove: (id: string) => void;
  onRenameCategory: (id: string, name: string) => void;
  onAddCategory: () => void;
}) {
  const [confirm, setConfirm] = useState<string | null>(null);
  /**
   * Какой цвет правится — на ТЕЛЕФОНЕ.
   *
   * На широком экране название и артикул правятся нажатием прямо по ним.
   * Пальцем попасть в строку из одиннадцати точек трудно, поэтому рядом с
   * корзиной стоит карандаш — тот же приём, что у карточки позиции.
   */
  const [editingColor, setEditingColor] = useState<string | null>(null);

  /** Служебный «Цвет не указан» стоит отдельно: он не редактируется и не удаляется. */
  const service = colors.filter((c) => c.unspecified);
  const known = new Set(categories.map((c) => c.id));
  const orphans = colors.filter((c) => !c.unspecified && (c.categoryId === null || !known.has(c.categoryId)));

  const card = (color: ColorRow) => (
    <div
      key={color.id}
      className="flex items-center gap-2 rounded-lg border px-3 py-2"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      {/* Миниатюра одна и она же кнопка редактора. Служебный цвет не правится. */}
      {color.unspecified ? (
        <Swatch color={color} size={22} />
      ) : (
        <ColorEditor
          color={{
            id: color.id,
            name: color.name,
            hex: color.hex,
            fill: color.fill ?? 'SOLID',
            glitterHex: color.glitterHex ?? '#FFFFFF',
          }}
          onChange={(patch) => onUpdate(color.id, patch)}
        />
      )}

      <div className="min-w-0 flex-1">
        {editingColor === color.id ? (
          /* Правка с телефона: два обычных поля вместо мелких строк. */
          <div className="space-y-1.5">
            <input
              autoFocus
              className="otc-input text-sm"
              placeholder="Название цвета"
              value={color.name}
              onChange={(e) => onUpdate(color.id, { name: e.target.value })}
            />
            <input
              className="otc-input tabular text-xs"
              placeholder="Артикул (необязательно)"
              value={color.art}
              onChange={(e) => onUpdate(color.id, { art: e.target.value })}
            />
          </div>
        ) : (
          <>
            {color.unspecified ? (
              <p className="text-sm text-ink">{color.name}</p>
            ) : (
              <EditableText
                value={color.name}
                onSave={(value) => onUpdate(color.id, { name: String(value) })}
                textClassName="text-sm"
                textStyle={{ color: 'var(--otc-ink)' }}
                inputWidth={160}
              />
            )}
            {!color.unspecified && (
              <EditableText
                value={color.art}
                onSave={(value) => onUpdate(color.id, { art: String(value) })}
                textClassName="tabular text-[11px]"
                textStyle={{ color: 'var(--otc-ink2)' }}
                placeholder="Без артикула"
                inputWidth={120}
              />
            )}
          </>
        )}
      </div>

      {!color.unspecified && (
        <div className="ml-auto flex flex-shrink-0 items-center gap-1">
          {/*
            Карандаш только на ТЕЛЕФОНЕ: на широком экране название и артикул
            правятся нажатием прямо по ним, и второй способ там был бы лишним.
          */}
          <button
            onClick={() => setEditingColor(editingColor === color.id ? null : color.id)}
            className="otc-btn otc-btn-ghost otc-btn-icon md:hidden"
            title={editingColor === color.id ? 'Готово' : 'Изменить название и артикул'}
          >
            {editingColor === color.id ? (
              <Check size={12} className="text-ink" />
            ) : (
              <Pencil size={12} className="text-ink2" />
            )}
          </button>
          {confirm === color.id ? (
            <TinyConfirm
              onConfirm={() => {
                onRemove(color.id);
                setConfirm(null);
              }}
              onCancel={() => setConfirm(null)}
            />
          ) : (
            <button onClick={() => setConfirm(color.id)} className="otc-btn otc-btn-ghost otc-btn-icon">
              <Trash2 size={12} className="text-ink2" />
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {categories.map((category) => {
        const list = colors.filter((c) => !c.unspecified && c.categoryId === category.id);
        return (
          <div key={category.id}>
            <div className="mb-2 flex items-center gap-2">
              <EditableText
                value={category.name}
                onSave={(value) => onRenameCategory(category.id, String(value))}
                textClassName="text-xs font-semibold uppercase tracking-wide"
                textStyle={{ color: 'var(--otc-ink2)' }}
                inputWidth={180}
              />
              <span className="text-xs text-ink2">({list.length})</span>
              <button
                onClick={() => onAdd(category.id)}
                className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs hover:bg-black/5"
                style={{ color: 'var(--otc-accent)' }}
              >
                <Plus size={12} /> Цвет
              </button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{list.map(card)}</div>
          </div>
        );
      })}

      {orphans.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink2">Без категории</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{orphans.map(card)}</div>
        </div>
      )}

      {service.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink2">Служебное</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{service.map(card)}</div>
          <p className="mt-1 text-[11px] text-ink2">
            «Цвет не указан» используется для заказов без выбранного цвета и не редактируется.
          </p>
        </div>
      )}

      <Button variant="secondary" onClick={onAddCategory}>
        <Plus size={13} /> Добавить категорию
      </Button>
    </div>
  );
}
